import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format, parseISO, isWithinInterval, startOfWeek, endOfWeek } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import {
  ArrowLeftRight,
  Bell,
  BarChart3,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Clock,
  Gift,
  PartyPopper,
  PlaneTakeoff,
  Star,
  Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import MobileLayout from "./MobileLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LeaveRequestDialog } from "@/components/leave/LeaveRequestDialog";
import {
  apiGetNotifications,
  apiGetNotificationUnreadCount,
  apiMarkNotificationRead,
  apiGetCalendarEvents,
  apiGetCelebrations,
  apiGetLeaveRequests,
  apiGetMyLeaveBalance,
  apiGetMyLeaveRequests,
  apiGetMyLeaveReplacementAssignments,
  type AppNotification,
  type CalendarEvent,
  type CelebrationItem,
  type LeaveRequest,
  type LeaveReplacementAssignment,
} from "@/lib/api";
import { getNotificationDisplayText } from "@/lib/notification-display";
import { mobileNotificationDestination, invalidateNotificationQueries } from "@/lib/mobile-notifications";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { getLeaveStatusLabel, getLeaveTypeLabel } from "@/lib/leave-i18n";

// ── Widget shell ─────────────────────────────────────────────────────────────
function Widget({
  title,
  unreadCount,
  linkTo,
  linkLabel,
  children,
}: {
  title: string;
  unreadCount?: number;
  linkTo?: string;
  linkLabel?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">{title}{!!unreadCount && <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">{unreadCount > 99 ? "99+" : unreadCount}</Badge>}</h2>
        {linkTo && (
          <Link
            to={linkTo}
            className="flex items-center gap-0.5 text-xs text-primary font-medium"
          >
            {linkLabel ?? t("mobile.home.seeAll")}
            <ChevronRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      <div className="px-4 py-3 space-y-0">{children}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-muted-foreground">
      <Icon className="h-7 w-7 opacity-40" />
      <p className="text-xs">{text}</p>
    </div>
  );
}

// ── Inbox widget ─────────────────────────────────────────────────────────────
function InboxWidget({ notifications }: { notifications: AppNotification[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: unreadCount } = useQuery({ queryKey: ["mobile-notif-count"], queryFn: apiGetNotificationUnreadCount, staleTime: 60_000 });
  const markRead = useMutation({ mutationFn: apiMarkNotificationRead, onSuccess: () => invalidateNotificationQueries(qc) });
  const unread = notifications.filter((n) => !n.isRead).slice(0, 3);
  return (
    <Widget title={t("mobile.home.inbox")} unreadCount={unreadCount?.count} linkTo="/mobile/inbox?tab=notifications">
      {unread.length === 0 ? (
        <EmptyState icon={Bell} text={t("mobile.home.allCaughtUp")} />
      ) : (
        unread.map((n) => {
          const display = getNotificationDisplayText(n, t);
          return (
            <button
              type="button"
              key={n.id}
              onClick={() => { markRead.mutate(n.id); navigate(mobileNotificationDestination(n)); }}
              className="w-[calc(100%+2rem)] text-left flex items-start gap-3 py-2.5 border-b border-border/30 last:border-0 cursor-pointer active:bg-muted/50 -mx-4 px-4 transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <Bell className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight truncate">
                  {display.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                  {display.message}
                </p>
              </div>
              {!n.isRead && (
                <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
              )}
            </button>
          );
        })
      )}
    </Widget>
  );
}

// ── This Week widget ──────────────────────────────────────────────────────────
type WeekItem =
  | { kind: "event"; data: CalendarEvent }
  | { kind: "leave"; data: LeaveRequest };

function ThisWeekWidget({
  events,
  leaves,
}: {
  events: CalendarEvent[];
  leaves: LeaveRequest[];
}) {
  const dateLocale = useDateLocale();
  const navigate = useNavigate();
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

  const items = useMemo<WeekItem[]>(() => {
    const evts: WeekItem[] = events
      .filter((e) => {
        try {
          return isWithinInterval(parseISO(e.date), { start: weekStart, end: weekEnd });
        } catch {
          return false;
        }
      })
      .map((e) => ({ kind: "event", data: e }));

    const lvs: WeekItem[] = leaves
      .filter((l) => {
        try {
          const s = parseISO(l.startDate);
          const en = parseISO(l.endDate);
          return s <= weekEnd && en >= weekStart;
        } catch {
          return false;
        }
      })
      .map((l) => ({ kind: "leave", data: l }));

    return [...evts, ...lvs]
      .sort((a, b) => {
        const da = a.kind === "event" ? a.data.date : a.data.startDate;
        const db = b.kind === "event" ? b.data.date : b.data.startDate;
        return da.localeCompare(db);
      })
      .slice(0, 5);
  }, [events, leaves, weekStart, weekEnd]);

  const CATEGORY_COLORS: Record<string, string> = {
    meeting: "bg-blue-500",
    announcement: "bg-amber-500",
    celebration: "bg-pink-500",
    company: "bg-purple-500",
    bank_holiday: "bg-red-500",
    other: "bg-slate-400",
  };

  const { t } = useTranslation();
  return (
    <Widget title={t("mobile.home.thisWeek")} linkTo="/mobile/calendar">
      {items.length === 0 ? (
        <EmptyState icon={CalendarDays} text={t("mobile.home.nothingScheduled")} />
      ) : (
        items.map((item, i) => {
          if (item.kind === "event") {
            const e = item.data;
            const color = CATEGORY_COLORS[e.category] ?? CATEGORY_COLORS.other;
            return (
              <div
                key={`ev-${e.id}`}
                onClick={() => navigate(`/mobile/calendar?event=${e.id}`)}
                className="flex items-center gap-3 py-2.5 border-b border-border/30 last:border-0 cursor-pointer active:bg-muted/50 -mx-4 px-4 transition-colors"
              >
                <span className={`h-2 w-2 rounded-full shrink-0 ${color}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{e.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(parseISO(e.date), "EEE, MMM d", { locale: dateLocale })}
                    {e.time ? ` · ${e.time}` : ""}
                  </p>
                </div>
              </div>
            );
          }
          const l = item.data;
          return (
            <div
              key={`lv-${l.id}-${i}`}
              className="flex items-center gap-3 py-2.5 border-b border-border/30 last:border-0"
            >
              <PlaneTakeoff className="h-4 w-4 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {l.employeeName} — {l.leaveType}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(parseISO(l.startDate), "MMM d", { locale: dateLocale })} –{" "}
                  {format(parseISO(l.endDate), "MMM d", { locale: dateLocale })}
                </p>
              </div>
            </div>
          );
        })
      )}
    </Widget>
  );
}

// ── People to Celebrate widget ────────────────────────────────────────────────
function CelebrationsWidget({ celebrations }: { celebrations: CelebrationItem[] }) {
  const { t } = useTranslation();
  const soon = useMemo(() => {
    const thisWeek = celebrations.filter((c) => c.daysAway <= 7);
    return (thisWeek.length > 0 ? thisWeek : celebrations.filter((c) => c.daysAway <= 30)).slice(0, 4);
  }, [celebrations]);

  return (
    <Widget title={t("mobile.home.celebrations")}>
      {soon.length === 0 ? (
        <EmptyState icon={PartyPopper} text={t("mobile.home.noCelebrations")} />
      ) : (
        soon.map((c) => (
          <div
            key={`${c.name}-${c.eventType}`}
            className="flex items-center gap-3 py-2.5 border-b border-border/30 last:border-0"
          >
            <EmployeeAvatar
              avatarUrl={c.avatarUrl}
              avatarColor={c.avatarColor}
              initials={c.initials}
              name={c.name}
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {c.eventType === "birthday" ? `🎂 ${t("mobile.home.todayLabel").replace("!", "") || "Birthday"}` : `🎉 ${t("dashboard.celebrations.anniversary") || "Work Anniversary"}`}
                {c.years ? ` · ${c.years} yr${c.years !== 1 ? "s" : ""}` : ""}
              </p>
            </div>
            <Badge
              variant={c.daysAway === 0 ? "default" : "secondary"}
              className="shrink-0 text-[10px]"
            >
              {c.daysAway === 0 ? t("mobile.home.todayLabel") : t("leave.duration", { count: c.daysAway })}
            </Badge>
          </div>
        ))
      )}
    </Widget>
  );
}

// ── Leave History widget ──────────────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  Approved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  Pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  Rejected: "bg-red-500/10 text-red-600 border-red-500/20",
  Cancelled: "bg-gray-500/10 text-gray-600 border-gray-500/20",
  "In Review": "bg-blue-500/10 text-blue-600 border-blue-500/20",
};
const PAGE_SIZE = 5;

function LeaveHistoryWidget({ requests }: { requests: LeaveRequest[] }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(requests.length / PAGE_SIZE);
  const slice = requests.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <Widget title={t("mobile.home.leaveHistory")}>
      {requests.length === 0 ? (
        <EmptyState icon={PlaneTakeoff} text={t("mobile.home.noLeaveHistory")} />
      ) : (
        <>
          {slice.map((r) => (
            <div
              key={r.id}
              onClick={() => navigate(`/mobile/calendar?request=${r.id}`)}
              className="flex items-start gap-3 py-2.5 border-b border-border/30 last:border-0 cursor-pointer active:bg-muted/50 -mx-4 px-4 transition-colors"
            >
              <PlaneTakeoff className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {getLeaveTypeLabel(t, r.leaveType, r.leaveTypeLabel)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {format(parseISO(r.startDate), "d MMM", { locale: dateLocale })}
                  {" – "}
                  {format(parseISO(r.endDate), "d MMM yyyy", { locale: dateLocale })}
                  {" · "}
                  {t("leave.duration", { count: r.workingDays })}
                </p>
              </div>
              <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[r.status] ?? ""}`}>
                {getLeaveStatusLabel(t, r.status)}
              </Badge>
            </div>
          ))}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="text-xs text-primary disabled:opacity-30 px-2 py-1"
              >
                ← {t("common.prev", "Prev")}
              </button>
              <span className="text-xs text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                className="text-xs text-primary disabled:opacity-30 px-2 py-1"
              >
                {t("common.next", "Next")} →
              </button>
            </div>
          )}
        </>
      )}
    </Widget>
  );
}

// ── Replacement Assignments widget ─────────────────────────────────────────
function ReplacementAssignmentsWidget({
  assignments,
}: {
  assignments: LeaveReplacementAssignment[];
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  if (assignments.length === 0) return null;

  function fmt(dateStr: string) {
    try {
      return format(new Date(`${dateStr}T00:00:00`), "d MMM yyyy", { locale: dateLocale });
    } catch {
      return dateStr;
    }
  }

  return (
    <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border/40">
        <div className="h-8 w-8 rounded-xl bg-sky-500/10 flex items-center justify-center shrink-0">
          <ArrowLeftRight className="h-4 w-4 text-sky-600" />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">
            {t("leave.replacementAssignmentsTitle")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("leave.replacementAssignmentsSubtitle")}
          </p>
        </div>
      </div>
      <div className="px-4 py-3 space-y-0">
        {assignments.map((assignment) => {
          const leaveTypeLabel = t(`leave.types.${assignment.leaveType}`, {
            defaultValue: assignment.leaveTypeLabel ?? assignment.leaveType,
          });
          return (
            <div
              key={assignment.id}
              className="py-2.5 border-b border-border/30 last:border-0 space-y-1.5"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-foreground leading-tight">
                  {t("leave.youAreReplacing", { employeeName: assignment.employeeName })}
                </p>
                <Badge variant="secondary" className="shrink-0 text-[10px]">
                  {t("leave.replacementStatus", {
                    status: getLeaveStatusLabel(t, assignment.status),
                  })}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("leave.replacementPeriod", {
                  startDate: fmt(assignment.startDate),
                  endDate: fmt(assignment.endDate),
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("leave.replacementLeaveType", { leaveType: leaveTypeLabel })}
              </p>
              {assignment.notes && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {t("leave.replacementNotes", { notes: assignment.notes })}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Administration widget ───────────────────────────────────────────────────
const ADMIN_CARDS = [
  { key: "recruitment", moduleKey: "recruitment", to: "/mobile/recruitment", icon: Briefcase },
  { key: "hrAnalytics", moduleKey: "hrAnalytics", to: "/mobile/hr-analytics", icon: BarChart3 },
  { key: "teamLeave", moduleKey: "teamLeave", to: "/mobile/team-leave", icon: CalendarDays },
  { key: "surveysAdmin", moduleKey: "surveys", to: "/mobile/surveys-admin", icon: ClipboardList },
  { key: "organization", moduleKey: "organization", to: "/mobile/organization", icon: Building2 },
] as const;

function AdminWidget() {
  const { t } = useTranslation();
  const { canAccess } = useModuleAccess();
  const cards = ADMIN_CARDS.filter((c) => canAccess(c.moduleKey));
  if (cards.length === 0) return null;

  return (
    <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <h2 className="text-sm font-semibold text-foreground">{t("mobile.home.administration")}</h2>
      </div>
      <div className="grid grid-cols-2 gap-3 p-4">
        {cards.map(({ key, to, icon: Icon }) => (
          <Link
            key={key}
            to={to}
            className="flex flex-col items-start gap-2 rounded-xl border border-border/60 p-3 active:bg-muted/50 transition-colors"
          >
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-foreground leading-tight">
              {t(`mobile.home.${key}`)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Greeting ──────────────────────────────────────────────────────────────────
function useGreeting() {
  const { t } = useTranslation();
  const h = new Date().getHours();
  if (h < 12) return t("mobile.home.morningGreeting");
  if (h < 17) return t("mobile.home.afternoonGreeting");
  return t("mobile.home.eveningGreeting");
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MobileHome() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { user } = useAuth();
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const greeting = useGreeting();
  const { canAccess } = useModuleAccess();

  const { data: notifData } = useQuery({
    queryKey: ["mobile-home-notifications"],
    queryFn: () => apiGetNotifications(10, 0),
    staleTime: 2 * 60_000,
  });

  const { data: calendarEvents = [] } = useQuery({
    queryKey: ["mobile-calendar-events"],
    queryFn: apiGetCalendarEvents,
    staleTime: 5 * 60_000,
  });

  const { data: celebrations = [] } = useQuery({
    queryKey: ["mobile-celebrations"],
    queryFn: apiGetCelebrations,
    staleTime: 10 * 60_000,
  });

  const { data: approvedLeaves = [] } = useQuery({
    queryKey: ["mobile-approved-leaves"],
    queryFn: () => apiGetLeaveRequests({ status: "Approved" }),
    enabled: canAccess("leaveManagement"),
    staleTime: 2 * 60_000,
  });

  const { data: leaveBalances = [] } = useQuery({
    queryKey: ["mobile-leave-balance"],
    queryFn: () => apiGetMyLeaveBalance(),
    enabled: canAccess("leaveManagement"),
    staleTime: 5 * 60_000,
  });

  const { data: myLeaveRequests = [] } = useQuery({
    queryKey: ["mobile-my-leave-requests"],
    queryFn: () => apiGetMyLeaveRequests(),
    enabled: canAccess("leaveManagement"),
    staleTime: 2 * 60_000,
  });

  const { data: replacementAssignments = [] } = useQuery({
    queryKey: ["mobile-leave-replacement-assignments"],
    queryFn: () => apiGetMyLeaveReplacementAssignments(),
    enabled: canAccess("leaveManagement"),
    staleTime: 2 * 60_000,
  });

  const isHrAdminOrCLevel =
    user?.role.mainRole === "hr" ||
    user?.role.mainRole === "admin" ||
    user?.role.subRole === "c_level";

  const notifications = notifData?.notifications ?? [];

  const homeHeader = (
    <div className="relative flex items-center w-full h-full">
      <img src="/besthr.png" alt="BestHR" className="h-7 object-contain shrink-0" />
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <p className="text-[11px] text-muted-foreground leading-none">
          {format(new Date(), "EEEE, dd MMMM", { locale: dateLocale })}
        </p>
        <p className="text-[15px] font-bold text-foreground leading-snug mt-0.5">
          {greeting}, {user?.name?.split(" ")[0] ?? "there"}
        </p>
      </div>
    </div>
  );

  return (
    <MobileLayout header={homeHeader}>
      <div className="px-4 space-y-4 pb-4 pt-4">
      {/* Inbox widget */}
      <InboxWidget notifications={notifications} />

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        {canAccess("leaveManagement") && <Button
          onClick={() => setShowLeaveDialog(true)}
          className="h-12 gradient-brand text-primary-foreground font-medium rounded-xl gap-2"
        >
          <Clock className="h-4 w-4" />
          {t("mobile.home.requestLeave")}
        </Button>}
        {canAccess("peopleDirectory") && <Link to="/mobile/people" className="contents">
          <Button
            variant="outline"
            className="h-12 w-full font-medium rounded-xl gap-2 border-border/60"
          >
            <Users className="h-4 w-4" />
            {t("mobile.home.peopleDirectory")}
          </Button>
        </Link>}
        {canAccess("timekeeping") && <Link to="/mobile/timekeeping" className="contents">
          <Button
            variant="outline"
            className="h-12 w-full font-medium rounded-xl gap-2 border-border/60 col-span-2"
          >
            <Clock className="h-4 w-4" />
            {t("mobile.home.logTime", { defaultValue: "Log time" })}
          </Button>
        </Link>}
      </div>

      {/* Leave Balance */}
      {canAccess("leaveManagement") && leaveBalances.length > 0 && (() => {
        const annual = leaveBalances.find(
          (b) => b.countsAgainstBalance && b.totalDays !== null
        );
        if (!annual) return null;
        const remaining = annual.availableNow ?? 0;
        const total = annual.totalDays ?? 0;
        const used = annual.usedDays;
        const pending = annual.pendingDays;
        return (
          <div className="bg-card border border-border/60 rounded-2xl px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-foreground">
                {t("mobile.home.leaveBalance")}
              </span>
              <span className="text-xs text-muted-foreground">{annual.year}</span>
            </div>
            <div className="flex items-end gap-1 mb-2">
              <span className="text-3xl font-bold text-primary leading-none">{remaining}</span>
              <span className="text-sm text-muted-foreground mb-0.5">/ {total} {t("mobile.home.days")}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5 mb-2">
              <div
                className="bg-primary rounded-full h-1.5 transition-all"
                style={{ width: `${total > 0 ? Math.min(100, (remaining / total) * 100) : 0}%` }}
              />
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>{t("leave.approvedDaysIncludingUpcoming")}: <span className="text-foreground font-medium">{used}</span></span>
              {pending > 0 && (
                <span>{t("mobile.home.pending")}: <span className="text-amber-500 font-medium">{pending}</span></span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Replacement Assignments */}
      {canAccess("leaveManagement") && <ReplacementAssignmentsWidget assignments={replacementAssignments} />}

      {/* Leave History */}
      {canAccess("leaveManagement") && <LeaveHistoryWidget requests={myLeaveRequests} />}

      {/* This Week widget */}
      {canAccess("leaveManagement") && <ThisWeekWidget events={calendarEvents} leaves={approvedLeaves} />}

      {/* People to Celebrate */}
      <CelebrationsWidget celebrations={celebrations} />

      {/* Administration (HR/Admin/C-Level only) */}
      {isHrAdminOrCLevel && <AdminWidget />}

      <LeaveRequestDialog
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
        balances={leaveBalances}
        currentEmployeeId={user?.employeeId ?? undefined}
      />
      </div>
    </MobileLayout>
  );
}
